/**
 * The identity a Bearer token belongs to (JOSH-89, R17).
 *
 * Decoded WITHOUT verifying the signature: it only decides whether a replay
 * stays with the same user and auth session, never whether to trust a token.
 * The refresh token rotates on every refresh, so it is never part of it.
 */

export interface JwtIdentity {
  sub: string;
  sessionId: string;
}

/** The token from an `Authorization: Bearer <token>` header value. */
export function bearerToken(authorization: string | null): string | null {
  const match = authorization?.match(/^Bearer\s+(\S+)$/i);
  return match ? match[1] : null;
}

/** `sub` and `session_id` of a JWT, or null when either is missing. */
export function jwtIdentity(token: string | null): JwtIdentity | null {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    // atob yields a binary string; non-ASCII claims decode as mojibake, which
    // is harmless here because only the ASCII `sub` and `session_id` are read.
    const claims = JSON.parse(atob(padded)) as Record<string, unknown>;
    const { sub, session_id: sessionId } = claims;
    if (typeof sub !== 'string' || !sub || typeof sessionId !== 'string' || !sessionId) return null;
    return { sub, sessionId };
  } catch {
    return null;
  }
}

/** Whether two tokens belong to the same user and auth session. */
export function sameIdentity(a: string | null, b: string | null): boolean {
  const left = jwtIdentity(a);
  const right = jwtIdentity(b);
  return !!left && !!right && left.sub === right.sub && left.sessionId === right.sessionId;
}
